import { noop } from 'underscore';
import React, { useCallback, useContext, useEffect, useState } from 'react';
import PropTypes from 'prop-types';
import classNames from 'classnames';
import { Button, Skeleton, SkeletonItem } from '@fluentui/react-components';
import { Constants } from '@bingads-webui-theme-2018/theme-fluent';
import { useResponsiveSize } from '@adscore/with-responsive-size';
import { I18nContext } from '@bingads-webui-react/i18n-context';
import { useDefaultStyles } from '@bingads-webui-react/with-default-styles';
import { getUrlWithProtocol } from '@bingads-webui-campaign-react/url-utils';

import {
  UAPPlacementTypeAlias as EPT,
  UAPLPlacementMetaList,
  getAdPlacementComponent,
} from '@bingads-webui-campaign-react/unified-ad-preview-library-core';
import { AdPlacementList, TextAdPlacementList, AdsDataMapping } from '@bingads-webui-campaign-react/pmax-landing-ad-preview';
import { extractWebsite, useFetchAssetRecommendation, getAdSuggestions, getLandingPageUrl } from '@bingads-webui-campaign-react/pmax-landing-utils';

import { MSIcon } from './icon';
import { getAdPreviewBannerStyles } from './ad-preview-banner-styles';

const getAdPlacement = (imageUrl, adData) => {
  if (!imageUrl && !adData) {
    return AdPlacementList[0];
  }
  if (!imageUrl) {
    return TextAdPlacementList[0];
  }
  return {
    placementMeta: UAPLPlacementMetaList[EPT.Responsive.MSAN.MSEdgeSingleCarousel],
    imageMeta: {
      url: imageUrl,
    },
  };
};

export function AdPreviewBanner({ logger, onCreateAdClick }) {
  const i18n = useContext(I18nContext);
  const classes = useDefaultStyles(getAdPreviewBannerStyles);
  const displayMode = useResponsiveSize(Constants.ResponsiveSizeBreakpoints);
  const website = extractWebsite();

  const [adSuggestions, setAdSuggestions] = useState();
  const [isLoading, setIsLoading] = useState(!!website);

  const { fetchAssetRecommendation } = useFetchAssetRecommendation();

  useEffect(() => {
    if (website && !adSuggestions) {
      logger.trace(`[SDK][AdPreviewBanner] Fetching asset recommendation for website: ${website}`);
      setIsLoading(true);

      fetchAssetRecommendation({ website }).then((res) => {
        const formattedAds = getAdSuggestions(res);
        setAdSuggestions(formattedAds);
        logger.trace(`[SDK][AdPreviewBanner] Loaded ad suggestions. Ad suggestions: ${JSON.stringify(formattedAds)}`);
      }).catch((err) => {
        logger.trace(`[SDK][AdPreviewBanner] Error loading ad suggestions. Error: ${JSON.stringify(err)}`);
      }).finally(() => {
        setIsLoading(false);
      });
    }
  }, [adSuggestions, fetchAssetRecommendation, website, logger]);

  const {
    imageUrl,
    adData,
  } = adSuggestions || {};

  const getAdDataForPlacement = useCallback((placementType) => {
    if (!adData) {
      return AdsDataMapping[placementType];
    }
    return {
      ...adData,
      displayUrl: website,
      website: getUrlWithProtocol(website),
    };
  }, [adData, website]);

  const onRenderPlacement = useCallback(() => {
    const adPlacement = getAdPlacement(imageUrl, adData);
    const { placementMeta } = adPlacement || {};
    const { placementType } = placementMeta || {};
    const AdPlacementComponent = getAdPlacementComponent(placementType);
    const placementAdData = getAdDataForPlacement(placementType);

    if (isLoading) {
      return (
        <div className="placement-shimmer">
          <Skeleton appearance="translucent" > <SkeletonItem style={{ height: '170px', width: '270px' }} /> </Skeleton>
        </div>
      );
    }

    return (
      <div className={placementType}>
        <AdPlacementComponent
          placementData={adPlacement}
          i18n={i18n}
          {...placementAdData}
        />
      </div>
    );
  }, [adData, getAdDataForPlacement, i18n, imageUrl, isLoading]);

  const onClickCreateAdCampaign = useCallback(() => {
    const landingPageUrl = getLandingPageUrl();
    logger.trace(`[SDK][AdPreviewBanner] Clicked create ad campaign. Opened landing page: ${landingPageUrl}`);
    if (onCreateAdClick) {
      onCreateAdClick();
    }
    window.open(landingPageUrl, '_blank');
  }, [logger, onCreateAdClick]);

  return (
    <div className={classNames('landing-page-preview-banner', classes.container)}>
      <div className={classNames(classes.branding, 'ms-branding')}>
        <MSIcon />
        <span className={classNames(classes.brandingText, 'branding-text')}>Microsoft Advertising</span>
      </div>
      <div className={classNames(classes.innerContainer, classes[`innerContainer-${displayMode}`])}>
        <div className={classNames('banner-slogan', classes.sloganContainer, classes[`sloganContainer-${displayMode}`])}>
          <h2 className="banner-title">
            {i18n.getString(_TL_('Promote your business for millions of your potential customer!'))}
          </h2>
          <p className="banner-description">
            {i18n.getString(_TL_('Millions of Bing users are searching for products and services. Attract more customers by providing your business offer on top of the Bing search and partners websites.'))}
          </p>
        </div>
        <div className={classNames('banner-ad-preview', classes.adPreviewContainer, classes[`adPreviewContainer-${displayMode}`])}>
          {onRenderPlacement()}
        </div>
      </div>
      <Button
        className={classNames('create-campaign-button', classes.createCampaignBtn)}
        onClick={onClickCreateAdCampaign}
        appearance="primary"
      >
        {i18n.getString(_TL_('Create ad campaign'))}
      </Button>
    </div>
  );
}

AdPreviewBanner.propTypes = {
  logger: PropTypes.shape({
    trace: PropTypes.func.isRequired,
  }),
  onCreateAdClick: PropTypes.func,
};

AdPreviewBanner.defaultProps = {
  logger: {
    trace: noop,
  },
  onCreateAdClick: undefined,
};
